// @ts-nocheck
// Full self-service Call Stats dashboard for the logged-in agent — premium/
// minimal design matching ct-admin-dashboard's visual language (flat KPI
// tiles, one semantic color palette, uniform chart cards).
//
import { useCallback, useMemo } from 'react';
import moment from 'moment';
import {
  FiPhone,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiPhoneCall,
  FiPhoneMissed,
  FiPhoneOff,
  FiClock,
  FiClipboard,
  FiCoffee,
} from 'react-icons/fi';
import Loader from '@/components/partials/components/Loader';
import { HAIRLINE } from '../ctiHelpers';
import PeriodCallsScatterModal from './PeriodCallsScatterModal';
import { useCallStatsData } from './call-stats/useCallStatsData';
import { formatDuration } from './call-stats/format';
import FilterBarPill from './call-stats/filters/FilterBarPill';
import StatTile from './call-stats/StatTile';
import ChartCard from './call-stats/ChartCard';
import PeriodOverviewRow from './call-stats/PeriodOverviewRow';
import {
  VolumeTrendChart,
  TalkTimeTrendChart,
  OutcomeDistributionDonut,
  DailyOutcomesBar,
} from './call-stats/charts';
import CallLogTable from './call-stats/CallLogTable';
import { getChartColors } from './call-stats/colors';

export function CallStatsBody({
  agentUsername = '',
  theme = 'light',
  showHeading = true,
  showCallLog = true,
  showQuickPeriods = true,
  showFilter = true,
  callLogExportLabel = 'Export',
  data,
}: any) {
  const isDark = theme === 'dark';
  const c = getChartColors(isDark);
  const title = agentUsername ? `Call Stats (${agentUsername})` : 'Call Stats';

  const {
    activePreset,
    setActivePreset,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    rangeData,
    totals,
    dailyFilled,
    periodCards,
    periodLoading,
    loadAll,
    tableRows,
    loading,
    recordsLoading,
    scatterPeriod,
    scatterCalls,
    scatterLoading,
    openPeriodScatter,
    closePeriodScatter,
    applyPreset,
    handleReset,
    handleRefresh,
  } = data;

  const rangeLabel = useMemo(() => {
    const from = moment(startDate);
    const to = moment(endDate);
    const span = from.isSame(to, 'day')
      ? from.format('D MMM YYYY')
      : `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
    const n = Number(totals.total) || 0;
    return `${span} · ${n} call${n === 1 ? '' : 's'}`;
  }, [startDate, endDate, totals.total]);

  const handlePresetChange = useCallback(
    (key) => {
      if (key) applyPreset(key);
    },
    [applyPreset],
  );

  const handleRangeChange = useCallback(
    (range) => {
      if (!range?.from) return;
      const end = range.to || range.from;
      setStartDate(range.from);
      setEndDate(end);
      setActivePreset(''); // a custom range means no preset tab is "active"
      if (range.to) loadAll(range.from, end);
    },
    [setStartDate, setEndDate, setActivePreset, loadAll],
  );

  return (
    <section className="cs-panel flex flex-col gap-3" data-theme={theme}>
      {showFilter ? (
        <div className={`cs-toolbar sticky top-10 z-[5] rounded-2xl border ${HAIRLINE} bg-card px-4 py-3 shadow-sm`}>
          {showHeading ? (
            <div className="mb-3">
              <h2 className="truncate text-[15px] font-semibold tracking-tight text-foreground">{title}</h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">Comprehensive call analytics for your activity</p>
            </div>
          ) : null}

          <FilterBarPill
            activePreset={activePreset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={handlePresetChange}
            onRangeChange={handleRangeChange}
            onReset={handleReset}
            onRefresh={handleRefresh}
            rangeLabel={rangeLabel}
          />
        </div>
      ) : null}

      {loading && !rangeData ? <Loader /> : null}

      {showQuickPeriods ? (
        <>
          <PeriodCallsScatterModal
            show={Boolean(scatterPeriod)}
            onHide={closePeriodScatter}
            periodLabel={scatterPeriod?.label || ''}
            calls={scatterCalls}
            loading={scatterLoading}
            theme={theme}
          />

          {/* Fixed periods — independent of the filter above, by design. */}
          <div>
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
                Quick periods
              </span>
              <span className="text-[10px] text-muted-foreground">Fixed — not affected by the filter</span>
            </div>
            <PeriodOverviewRow
              periodCards={periodCards}
              isDark={isDark}
              onChartClick={openPeriodScatter}
              activeKey={scatterPeriod?.key}
              loading={periodLoading}
            />
          </div>
        </>
      ) : null}

      <div>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
            Selected range
          </span>
          <span className="text-[10px] text-muted-foreground">{rangeLabel}</span>
        </div>

        {/* 3-across at most normal widths (this page sits beside the Dialer
          * rail, so 9-across only gets room on very wide screens). */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 2xl:grid-cols-9">
          <StatTile icon={FiPhone} label="Total Calls" value={totals.total ?? 0} sub={`${totals.answered_pct ?? 0}% answered`} color={c.hex.call} />
          <StatTile icon={FiArrowDownLeft} label="Incoming" value={totals.total_incoming ?? 0} color={c.direction[0]} />
          <StatTile icon={FiArrowUpRight} label="Outgoing" value={totals.total_outgoing ?? 0} color={c.direction[1]} />
          <StatTile icon={FiPhoneCall} label="Answered" value={totals.answered ?? 0} tone="good" />
          <StatTile icon={FiPhoneOff} label="Failed" value={totals.failed ?? 0} tone="crit" />
          <StatTile icon={FiPhoneMissed} label="Abandoned" value={totals.abandoned ?? 0} tone="warn" />
          <StatTile icon={FiClock} label="Talk Time" value={formatDuration(totals.total_talk_time)} color={c.periodAccents.this_week} />
          <StatTile
            icon={FiClipboard}
            label="Wrap Up"
            value={formatDuration(totals.total_wrap_seconds)}
            sub={`Avg ${formatDuration(totals.avg_wrap_seconds)}`}
            color={c.periodAccents.yesterday}
          />
          <StatTile
            icon={FiCoffee}
            label="Breaks"
            value={totals.breaks_taken ?? 0}
            sub={`${formatDuration(totals.total_break_seconds)} · Avg ${formatDuration(totals.avg_break_seconds)}`}
            color="#0891b2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-2">
        <ChartCard title="Incoming vs outgoing" eyebrow="Call volume" subtitle={rangeLabel}>
          <VolumeTrendChart dailyFilled={dailyFilled} isDark={isDark} />
        </ChartCard>

        <ChartCard title="Outcome Distribution" eyebrow="Outcomes" subtitle="Answered · Failed · Abandoned">
          <OutcomeDistributionDonut totals={totals} isDark={isDark} />
        </ChartCard>

        <ChartCard title="Daily Outcomes" eyebrow="Outcomes" subtitle="Per day, stacked by result">
          <DailyOutcomesBar dailyFilled={dailyFilled} isDark={isDark} />
        </ChartCard>

        <ChartCard title="Talk Time Trend" eyebrow="Talk time" subtitle="Daily total, with your average">
          <TalkTimeTrendChart dailyFilled={dailyFilled} isDark={isDark} />
        </ChartCard>
      </div>

      {showCallLog ? (
        <ChartCard title="Recent Call Log" eyebrow="Activity">
          <CallLogTable
            rows={tableRows}
            loading={recordsLoading}
            serverPaging={data.callLogPaging}
            onSearchChange={data.changeCallLogSearch}
            fetchExportRows={data.fetchCallLogExport}
            exportLabel={callLogExportLabel}
          />
        </ChartCard>
      ) : null}
    </section>
  );
}

export default function AgentCallStatsPanel({
  onClose,
  agentId,
  agentUsername = '',
  theme = 'light',
  showHeading = true,
  showCallLog = true,
  showQuickPeriods = true,
  showFilter = true,
}) {
  const data = useCallStatsData(agentId);
  return (
    <CallStatsBody
      agentUsername={agentUsername}
      theme={theme}
      showHeading={showHeading}
      showCallLog={showCallLog}
      showQuickPeriods={showQuickPeriods}
      showFilter={showFilter}
      data={data}
    />
  );
}
